import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Users, MapPin, Globe, Clock, MessageCircle, 
  Heart, Shield, Lock, Check, X, Loader2,
  Mail, Eye, EyeOff, ChevronLeft, Send, UserPlus,
  AlertTriangle, Ban, Flag
} from "lucide-react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "../../contexts/AuthContext";
import { Section, Button } from "./PageComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============== AUTH FORMS ==============

export const LoginPage = ({ onSuccess }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      await login(email, password);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="login">
      <div className="max-w-md mx-auto" data-testid="login-page">
        <a href="#penpal" className="inline-flex items-center text-xs uppercase tracking-widest text-lws-taupe hover:text-lws-green mb-8 transition-colors">
          <ChevronLeft size={16} className="mr-2" /> Back to Pen Pals
        </a>
        
        <h1 className="text-4xl md:text-5xl font-serif mb-4 text-lws-green">Welcome back</h1>
        <p className="text-lws-taupe mb-8">Sign in to continue your correspondence journey.</p>
        
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
          <div>
            <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
              data-testid="login-email"
            />
          </div>
          
          <div>
            <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-lws-ink/10 px-4 py-3 pr-12 focus:outline-none focus:border-lws-green"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-lws-taupe hover:text-lws-green"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          
          {error && (
            <p className="text-red-600 text-sm" data-testid="login-error">{error}</p>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-lws-green text-lws-paper py-4 text-xs uppercase tracking-widest font-medium hover:bg-lws-navy transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="login-submit"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in...</> : "Sign In"}
          </button>
        </form>
        
        <div className="mt-8 text-center space-y-4">
          <a href="#forgot-password" className="text-sm text-lws-amber hover:text-lws-green transition-colors">
            Forgot your password?
          </a>
          <p className="text-sm text-lws-taupe">
            Don't have an account? <a href="#register" className="text-lws-green hover:underline">Create one</a>
          </p>
        </div>
      </div>
    </Section>
  );
};

export const RegisterPage = ({ onSuccess, signupSource = "penpal" }) => {
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Determine page text based on signup source
  const pageConfig = {
    penpal: {
      title: "Join the Society",
      subtitle: "Create an account to find your pen pal match.",
      backLink: "#penpal",
      backText: "Back to Pen Pals"
    },
    course: {
      title: "Create Your Account",
      subtitle: "Sign up to access The Correspondence Method course.",
      backLink: "#courses",
      backText: "Back to Courses"
    },
    membership: {
      title: "Join the Society",
      subtitle: "Create an account to explore membership options.",
      backLink: "#membership",
      backText: "Back to Membership"
    },
    general: {
      title: "Join the Society",
      subtitle: "Create an account to get started.",
      backLink: "#home",
      backText: "Back to Home"
    }
  };

  const config = pageConfig[signupSource] || pageConfig.general;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      await register(email, password, name, signupSource);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="register">
      <div className="max-w-md mx-auto" data-testid="register-page">
        <a href={config.backLink} className="inline-flex items-center text-xs uppercase tracking-widest text-lws-taupe hover:text-lws-green mb-8 transition-colors">
          <ChevronLeft size={16} className="mr-2" /> {config.backText}
        </a>
        
        <h1 className="text-4xl md:text-5xl font-serif mb-4 text-lws-green">{config.title}</h1>
        <p className="text-lws-taupe mb-8">{config.subtitle}</p>
        
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
          <div>
            <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Your Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
              data-testid="register-name"
            />
          </div>
          
          <div>
            <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
              data-testid="register-email"
            />
          </div>
          
          <div>
            <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full border border-lws-ink/10 px-4 py-3 pr-12 focus:outline-none focus:border-lws-green"
                data-testid="register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-lws-taupe hover:text-lws-green"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-lws-taupe mt-1">Minimum 8 characters</p>
          </div>
          
          <div>
            <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Confirm Password</label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
              data-testid="register-confirm-password"
            />
          </div>
          
          {error && (
            <p className="text-red-600 text-sm" data-testid="register-error">{error}</p>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-lws-green text-lws-paper py-4 text-xs uppercase tracking-widest font-medium hover:bg-lws-navy transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="register-submit"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Creating account...</> : "Create Account"}
          </button>
        </form>
        
        <p className="mt-8 text-center text-sm text-lws-taupe">
          Already have an account? <a href="#login" className="text-lws-green hover:underline">Sign in</a>
        </p>
      </div>
    </Section>
  );
};

// ============== PEN PAL LANDING ==============

export const PenPalLandingPage = () => {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/penpal/stats`);
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  return (
    <>
      <Section id="penpal">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-24 items-center">
          <div className="space-y-10">
            <span className="text-xs uppercase tracking-[0.4em] text-lws-amber font-semibold">Pen Pal Program</span>
            <h1 className="text-5xl md:text-7xl leading-tight font-serif">
              Find your <span className="italic">letter writing</span> companion.
            </h1>
            <p className="text-xl text-lws-taupe font-serif leading-relaxed">
              Connect with fellow letter enthusiasts from around the world. 
              Our privacy-first matching system helps you find the perfect pen pal 
              based on your interests, language, and writing frequency.
            </p>
            
            <div className="grid grid-cols-3 gap-6 py-8 border-y border-lws-ink/5">
              <div className="text-center">
                <p className="text-4xl font-serif text-lws-green">{stats?.total_active_profiles || "—"}</p>
                <p className="text-xs uppercase tracking-widest text-lws-taupe mt-2">Active Members</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-serif text-lws-green">{stats?.total_successful_matches || "—"}</p>
                <p className="text-xs uppercase tracking-widest text-lws-taupe mt-2">Matches Made</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-serif text-lws-green">{stats?.top_countries?.length || "—"}+</p>
                <p className="text-xs uppercase tracking-widest text-lws-taupe mt-2">Countries</p>
              </div>
            </div>
            
            {loading ? (
              <div className="flex items-center gap-2 text-lws-taupe">
                <Loader2 size={20} className="animate-spin" /> Loading...
              </div>
            ) : user ? (
              <div className="flex flex-col sm:flex-row gap-4">
                {user.penpal_profile_id ? (
                  <>
                    <Button href="#penpal-browse">Browse Pen Pals</Button>
                    <Button variant="outline" href="#penpal-profile">My Profile</Button>
                  </>
                ) : (
                  <Button href="#penpal-create">Create Your Profile</Button>
                )}
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4">
                <Button href="#register">Join the Program</Button>
                <Button variant="outline" href="#login">Sign In</Button>
              </div>
            )}
          </div>
          
          <div className="relative">
            <div className="aspect-[4/5] bg-lws-taupe/10 overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1579762715118-a6f1d4b934f1?w=800&h=1000&fit=crop" 
                alt="Pen Pal Program" 
                className="w-full h-full object-cover opacity-80"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </Section>
      
      {/* Privacy & Security Section */}
      <Section className="bg-lws-green text-lws-paper">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif mb-4">Privacy First</h2>
          <p className="text-lws-paper/70 max-w-2xl mx-auto text-lg">
            Your safety is our priority. We've built multiple layers of protection 
            into our pen pal matching system.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            { 
              icon: Lock, 
              title: "Encrypted Addresses", 
              desc: "Your physical address is encrypted and only shared after mutual consent." 
            },
            { 
              icon: Shield, 
              title: "Mutual Match Required", 
              desc: "Both parties must accept before any personal information is exchanged." 
            },
            { 
              icon: Mail, 
              title: "Forwarding Service", 
              desc: "Opt to receive letters via our PO Box—your home address stays private." 
            },
          ].map((item, idx) => (
            <div key={idx} className="text-center space-y-4">
              <div className="w-16 h-16 bg-lws-paper/10 rounded-full flex items-center justify-center mx-auto">
                <item.icon className="text-lws-amber" size={28} />
              </div>
              <h3 className="text-2xl font-serif">{item.title}</h3>
              <p className="text-lws-paper/70">{item.desc}</p>
            </div>
          ))}
        </div>
        
        <div className="mt-16 p-8 bg-lws-paper/10 rounded-lg max-w-3xl mx-auto">
          <div className="flex items-start gap-4">
            <AlertTriangle className="text-lws-amber flex-shrink-0 mt-1" size={24} />
            <div>
              <h4 className="font-bold text-lg mb-2">Your Control, Your Choice</h4>
              <p className="text-lws-paper/80 text-sm">
                You can withdraw from the program at any time. Deleting your profile 
                removes all your data, including connections. You can also block or 
                report users who make you uncomfortable.
              </p>
            </div>
          </div>
        </div>
      </Section>
      
      {/* How It Works */}
      <Section>
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif mb-4">How It Works</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {[
            { step: "01", title: "Create Profile", desc: "Tell us about yourself, your interests, and what you're looking for." },
            { step: "02", title: "Browse & Connect", desc: "Explore profiles and send connection requests to potential pen pals." },
            { step: "03", title: "Mutual Match", desc: "When both parties accept, you'll see each other's mailing address." },
            { step: "04", title: "Start Writing", desc: "Send your first letter and begin your correspondence journey!" },
          ].map((item, idx) => (
            <div key={idx} className="text-center">
              <div className="text-6xl font-serif text-lws-amber/30 mb-4">{item.step}</div>
              <h3 className="text-xl font-serif mb-2">{item.title}</h3>
              <p className="text-sm text-lws-taupe">{item.desc}</p>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
};

// ============== CREATE PROFILE ==============

export const CreatePenPalProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    display_name: "",
    bio: "",
    country: "",
    location_preference: "no_preference",
    writing_frequency: "monthly",
    languages: [],
    interests: [],
    use_forwarding_service: false,
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    address_country: "",
  });
  
  const [newLanguage, setNewLanguage] = useState("");
  const [newInterest, setNewInterest] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const addLanguage = () => {
    if (newLanguage && !formData.languages.includes(newLanguage)) {
      setFormData(prev => ({ ...prev, languages: [...prev.languages, newLanguage] }));
      setNewLanguage("");
    }
  };

  const removeLanguage = (lang) => {
    setFormData(prev => ({ ...prev, languages: prev.languages.filter(l => l !== lang) }));
  };

  const addInterest = () => {
    if (newInterest && !formData.interests.includes(newInterest) && formData.interests.length < 10) {
      setFormData(prev => ({ ...prev, interests: [...prev.interests, newInterest] }));
      setNewInterest("");
    }
  };

  const removeInterest = (interest) => {
    setFormData(prev => ({ ...prev, interests: prev.interests.filter(i => i !== interest) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/penpal/profile`, formData, { withCredentials: true });
      setSuccess(true);
      await checkAuth();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">Please sign in first</h2>
          <Button href="#login">Sign In</Button>
        </div>
      </Section>
    );
  }

  if (success) {
    return (
      <Section id="penpal-create">
        <div className="max-w-2xl mx-auto text-center" data-testid="profile-success">
          <div className="w-20 h-20 bg-lws-green rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="text-lws-paper" size={40} />
          </div>
          <h1 className="text-4xl md:text-5xl font-serif mb-4 text-lws-green">Profile Created!</h1>
          <p className="text-xl text-lws-taupe mb-8">
            You're now part of the Letter Writers Society pen pal program. 
            Start browsing profiles to find your perfect correspondence partner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="#penpal-browse">Browse Pen Pals</Button>
            <Button variant="outline" href="#penpal-profile">View My Profile</Button>
          </div>
        </div>
      </Section>
    );
  }

  return (
    <Section id="penpal-create">
      <div className="max-w-2xl mx-auto" data-testid="create-profile-page">
        <a href="#penpal" className="inline-flex items-center text-xs uppercase tracking-widest text-lws-taupe hover:text-lws-green mb-8 transition-colors">
          <ChevronLeft size={16} className="mr-2" /> Back to Pen Pals
        </a>
        
        <h1 className="text-4xl md:text-5xl font-serif mb-4 text-lws-green">Create Your Profile</h1>
        <p className="text-lws-taupe mb-8">Tell us about yourself to find your ideal pen pal match.</p>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-12">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                step >= s ? 'bg-lws-green text-lws-paper' : 'bg-lws-taupe/10 text-lws-taupe'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`w-24 h-0.5 mx-2 ${step > s ? 'bg-lws-green' : 'bg-lws-taupe/10'}`} />}
            </div>
          ))}
        </div>
        
        <form onSubmit={handleSubmit} data-testid="create-profile-form">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <h2 className="text-2xl font-serif mb-6">About You</h2>
              
              <div>
                <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Display Name *</label>
                <input
                  type="text"
                  name="display_name"
                  value={formData.display_name}
                  onChange={handleChange}
                  required
                  maxLength={50}
                  placeholder="How you'd like to be known"
                  className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
                  data-testid="profile-display-name"
                />
                <p className="text-xs text-lws-taupe mt-1">This is what other members will see</p>
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Bio *</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  required
                  minLength={10}
                  maxLength={500}
                  rows={4}
                  placeholder="Tell potential pen pals about yourself, what you enjoy writing about, and what you're looking for..."
                  className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
                  data-testid="profile-bio"
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Country *</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Australia, United States, Japan"
                  className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
                  data-testid="profile-country"
                />
              </div>
              
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!formData.display_name || !formData.bio || !formData.country}
                className="w-full bg-lws-green text-lws-paper py-4 text-xs uppercase tracking-widest font-medium hover:bg-lws-navy transition-colors disabled:opacity-50"
              >
                Continue
              </button>
            </motion.div>
          )}
          
          {/* Step 2: Preferences */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <h2 className="text-2xl font-serif mb-6">Your Preferences</h2>
              
              <div>
                <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Location Preference</label>
                <select
                  name="location_preference"
                  value={formData.location_preference}
                  onChange={handleChange}
                  className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green bg-white"
                  data-testid="profile-location-pref"
                >
                  <option value="no_preference">No preference</option>
                  <option value="same_country">Same country</option>
                  <option value="international">International only</option>
                  <option value="nearby">Nearby (regional)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Writing Frequency</label>
                <select
                  name="writing_frequency"
                  value={formData.writing_frequency}
                  onChange={handleChange}
                  className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green bg-white"
                  data-testid="profile-frequency"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every two weeks</option>
                  <option value="monthly">Monthly</option>
                  <option value="occasional">Occasional (when inspired)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Languages *</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newLanguage}
                    onChange={(e) => setNewLanguage(e.target.value)}
                    placeholder="Add a language"
                    className="flex-1 border border-lws-ink/10 px-4 py-2 focus:outline-none focus:border-lws-green"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
                  />
                  <button
                    type="button"
                    onClick={addLanguage}
                    className="px-4 py-2 bg-lws-green text-lws-paper text-sm hover:bg-lws-navy"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.languages.map(lang => (
                    <span key={lang} className="bg-lws-taupe/10 px-3 py-1 text-sm flex items-center gap-2">
                      {lang}
                      <button type="button" onClick={() => removeLanguage(lang)} className="text-lws-taupe hover:text-red-500">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Interests * (up to 10)</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newInterest}
                    onChange={(e) => setNewInterest(e.target.value)}
                    placeholder="Add an interest"
                    className="flex-1 border border-lws-ink/10 px-4 py-2 focus:outline-none focus:border-lws-green"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                  />
                  <button
                    type="button"
                    onClick={addInterest}
                    disabled={formData.interests.length >= 10}
                    className="px-4 py-2 bg-lws-green text-lws-paper text-sm hover:bg-lws-navy disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.interests.map(interest => (
                    <span key={interest} className="bg-lws-amber/20 px-3 py-1 text-sm flex items-center gap-2">
                      {interest}
                      <button type="button" onClick={() => removeInterest(interest)} className="text-lws-taupe hover:text-red-500">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 border border-lws-ink/20 py-4 text-xs uppercase tracking-widest font-medium hover:border-lws-green"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  disabled={formData.languages.length === 0 || formData.interests.length === 0}
                  className="flex-1 bg-lws-green text-lws-paper py-4 text-xs uppercase tracking-widest font-medium hover:bg-lws-navy transition-colors disabled:opacity-50"
                >
                  Continue
                </button>
              </div>
            </motion.div>
          )}
          
          {/* Step 3: Address */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <h2 className="text-2xl font-serif mb-2">Mailing Address</h2>
              <p className="text-lws-taupe text-sm mb-6">
                Your address is <strong>encrypted</strong> and only shared with mutual matches. 
                You can also use our forwarding service to keep your home address completely private.
              </p>
              
              <div className="bg-lws-amber/10 p-6 rounded-lg">
                <label className="flex items-start gap-4 cursor-pointer">
                  <input
                    type="checkbox"
                    name="use_forwarding_service"
                    checked={formData.use_forwarding_service}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 accent-lws-green"
                    data-testid="profile-forwarding"
                  />
                  <div>
                    <span className="font-medium text-lws-ink">Use Letter Writers Society Forwarding Service</span>
                    <p className="text-sm text-lws-taupe mt-1">
                      Your pen pals will send letters to our PO Box. We'll forward them to you. 
                      Your home address stays completely private.
                    </p>
                  </div>
                </label>
              </div>
              
              {!formData.use_forwarding_service && (
                <div className="space-y-4 border-t border-lws-ink/5 pt-6">
                  <p className="text-sm text-lws-taupe flex items-center gap-2">
                    <Lock size={16} /> This information is encrypted and only visible to mutual matches
                  </p>
                  
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Address Line 1 *</label>
                    <input
                      type="text"
                      name="address_line1"
                      value={formData.address_line1}
                      onChange={handleChange}
                      required={!formData.use_forwarding_service}
                      placeholder="Street address"
                      className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
                      data-testid="profile-address1"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Address Line 2</label>
                    <input
                      type="text"
                      name="address_line2"
                      value={formData.address_line2}
                      onChange={handleChange}
                      placeholder="Apartment, suite, etc. (optional)"
                      className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required={!formData.use_forwarding_service}
                        className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
                        data-testid="profile-city"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">State/Province</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Postal Code *</label>
                      <input
                        type="text"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleChange}
                        required={!formData.use_forwarding_service}
                        className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
                        data-testid="profile-postal"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Country *</label>
                      <input
                        type="text"
                        name="address_country"
                        value={formData.address_country}
                        onChange={handleChange}
                        required={!formData.use_forwarding_service}
                        className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green"
                        data-testid="profile-address-country"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {error && (
                <p className="text-red-600 text-sm" data-testid="profile-error">{error}</p>
              )}
              
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 border border-lws-ink/20 py-4 text-xs uppercase tracking-widest font-medium hover:border-lws-green"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || (!formData.use_forwarding_service && (!formData.address_line1 || !formData.city || !formData.postal_code || !formData.address_country))}
                  className="flex-1 bg-lws-green text-lws-paper py-4 text-xs uppercase tracking-widest font-medium hover:bg-lws-navy transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="profile-submit"
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Creating...</> : "Create Profile"}
                </button>
              </div>
            </motion.div>
          )}
        </form>
      </div>
    </Section>
  );
};

// Export the browse and connections pages
export { default as BrowsePenPalsPage } from "./BrowsePenPals";
export { default as MyPenPalProfilePage } from "./MyPenPalProfile";
export { default as PenPalConnectionsPage } from "./PenPalConnections";
